/**
 * Werving — acties op de sollicitantkaart (alleen met recht Werving bewerken), feedback Joanne 8 okt 2026:
 *
 *  - KoppelVelden: de sollicitatie handmatig aan een vacature koppelen, ook als de AI hem al had gekoppeld, en bij
 *    stages het stagetype kiezen. De server legt vast dat het handmatig is (koppeling/stagetype_bron = 'mens'),
 *    zodat een volgende AI-run het niet terugdraait. Vangnet: ongedaan maken (toast + Ctrl/Cmd+Z) zet de vorige
 *    waarde terug; staan er al beoordelingen op de oude vacature, dan eerst bevestigen.
 *  - Antwoordadres: waar een concept naartoe gaat (formulier, Reply-To of doorgestuurde mail — niet blind de
 *    afzender). Bewerkbaar; ongedaan maken zet het vorige adres terug. Er wordt nooit iets verstuurd: het adres wordt
 *    alleen de ontvanger van een concept in ikwilwerken@, dat Joanne zelf verstuurt.
 */
import React, { useState } from 'react';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { bevestig } from '../../utils/bevestig';
import { wervingApi } from './wervingApi';
import { ACCENT, INKT, LIJN, STIL, koppelOpties } from './wervingLogica';

const GEEN = '';

export function KoppelVelden({ k, sid, vacatures, stagetypes, onKlaar }) {
  const [bezig, setBezig] = useState(false);
  const huidig = k.vacature?.id ?? GEEN;
  const opties = koppelOpties(vacatures, k.vacature?.id);
  const types = (stagetypes || []).filter((t) => !t.gearchiveerd || t.sleutel === k.stagetype);
  const toonStage = k.categorie === 'stage' || k.vacature?.soort === 'open' || !!k.stagetype;
  const titel = (id) => opties.find((v) => v.id === id)?.titel || 'vacature';

  const koppel = async (waarde) => {
    const naar = waarde === GEEN ? null : Number(waarde);
    const vorige = k.vacature?.id ?? null;
    if (naar === vorige || bezig) return;
    const n = Object.values(k.telling || {}).reduce((a, x) => a + (x || 0), 0);
    if (vorige && n > 0 && !(await bevestig({
      titel: naar ? `Naar ${titel(naar)}?` : 'Koppeling weghalen?',
      tekst: `Er ${n === 1 ? 'staat al 1 beoordeling' : `staan al ${n} beoordelingen`} op ${k.vacature.titel}. Die blijven bewaard, `
        + 'maar passen niet bij de scorecard van een andere vacature.',
      knop: naar ? 'Verplaatsen' : 'Weghalen',
    }))) return;
    setBezig(true);
    try {
      await wervingApi.koppel(sid, naar);
      biedOngedaanAan(naar ? `Gekoppeld aan ${titel(naar)}` : 'Koppeling weggehaald', async () => {
        await wervingApi.koppel(sid, vorige);
        await onKlaar();
      });
      await onKlaar();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const zetType = async (waarde) => {
    const naar = waarde === GEEN ? null : waarde;
    const vorige = k.stagetype || null;
    if (naar === vorige || bezig) return;
    setBezig(true);
    try {
      await wervingApi.stagetype(sid, naar);
      const label = types.find((t) => t.sleutel === naar)?.label;
      biedOngedaanAan(naar ? `Stagetype: ${label}` : 'Stagetype weggehaald', async () => {
        await wervingApi.stagetype(sid, vorige);
        await onKlaar();
      });
      await onKlaar();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  return (
    <>
      <TextField select size="small" label={k.vacature ? 'Vacature' : 'Koppel aan vacature'} value={huidig} disabled={bezig}
        onChange={(e) => koppel(e.target.value)} sx={{ minWidth: 230 }}
        helperText={k.koppeling === 'ai' ? 'gekoppeld door de AI' : k.koppeling === 'mens' ? 'handmatig gekoppeld' : undefined}
        FormHelperTextProps={{ sx: { mx: 0.5, mt: 0.25, fontSize: 11.5 } }}>
        {!k.vacature && <MenuItem value={GEEN} disabled>Kies een vacature</MenuItem>}
        {opties.map((v) => <MenuItem key={v.id} value={v.id}>{v.titel}{v.status === 'gesloten' ? ' (gesloten)' : ''}</MenuItem>)}
        {k.vacature && <MenuItem value={GEEN} sx={{ color: STIL }}>Geen vacature (te koppelen)</MenuItem>}
      </TextField>
      {toonStage && types.length > 0 && (
        <TextField select size="small" label="Stagetype" value={k.stagetype || GEEN} disabled={bezig}
          onChange={(e) => zetType(e.target.value)} sx={{ minWidth: 220 }}
          helperText={k.stagetype_bron === 'ai' ? 'ingedeeld door de AI' : k.stagetype_bron === 'trefwoord' ? 'ingedeeld op trefwoorden'
            : k.stagetype_bron === 'mens' ? 'handmatig ingedeeld' : undefined}
          FormHelperTextProps={{ sx: { mx: 0.5, mt: 0.25, fontSize: 11.5 } }}>
          <MenuItem value={GEEN} sx={{ color: STIL }}>Geen stagetype</MenuItem>
          {types.map((t) => <MenuItem key={t.sleutel} value={t.sleutel}>{t.label}</MenuItem>)}
        </TextField>
      )}
    </>
  );
}

export function Antwoordadres({ k, sid, afgeleid, onKlaar }) {
  const [bewerk, setBewerk] = useState(false);
  const [waarde, setWaarde] = useState('');
  const [bezig, setBezig] = useState(false);
  const opgeslagen = k.antwoordadres || null;
  // Net gewist terwijl de brief nog het oude (opgeslagen) adres gaf: dan bepaalt de server het bij het klaarzetten.
  const verouderd = !opgeslagen && afgeleid?.opgeslagen;
  const adres = opgeslagen || (verouderd ? null : afgeleid?.adres) || null;
  const bron = opgeslagen ? k.antwoordadres_bron_label : afgeleid?.bron_label;

  const open = () => { setWaarde(adres || ''); setBewerk(true); };
  const bewaar = async () => {
    const nieuw = waarde.trim();
    if ((nieuw || null) === opgeslagen) { setBewerk(false); return; }
    setBezig(true);
    try {
      const r = await wervingApi.antwoordadres(sid, nieuw || null);
      setBewerk(false);
      biedOngedaanAan(nieuw ? 'Antwoordadres opgeslagen' : 'Antwoordadres gewist', async () => {
        await wervingApi.antwoordadres(sid, r.vorige || null);
        await onKlaar();
      });
      await onKlaar();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  if (bewerk) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
        <TextField size="small" autoFocus label="Concept gaat naar" placeholder="naam@voorbeeld.nl" value={waarde}
          onChange={(e) => setWaarde(e.target.value)} sx={{ minWidth: 280 }}
          onKeyDown={(e) => { if (e.key === 'Enter') bewaar(); if (e.key === 'Escape') setBewerk(false); }}
          inputProps={{ 'aria-label': 'Antwoordadres', maxLength: 254 }} />
        <Button size="small" onClick={bewaar} disabled={bezig} sx={{ textTransform: 'none', color: ACCENT }}>Opslaan</Button>
        <Button size="small" onClick={() => setBewerk(false)} sx={{ textTransform: 'none', color: STIL }}>Annuleren</Button>
        <Typography sx={{ fontSize: 12, color: STIL, width: '100%' }}>
          Alleen de ontvanger van een concept in ikwilwerken@; versturen doe je zelf in Outlook. Leeg = terug naar wat de mail zegt.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25, flexWrap: 'wrap' }}>
      <Typography sx={{ fontSize: 13, color: STIL }}>Concept gaat naar</Typography>
      {adres ? (
        <Box component="span" sx={{ fontSize: 13, color: INKT, border: `1px solid ${LIJN}`, borderRadius: 10, px: 1, py: 0.2 }}>
          {adres}{bron ? <Box component="span" sx={{ color: STIL }}> · {bron}</Box> : null}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 13, color: '#b45309' }}>
          {afgeleid === undefined ? '…' : verouderd ? 'het adres uit de mail (bepaald bij het klaarzetten)'
            : 'geen adres van de sollicitant gevonden — vul het in'}
        </Typography>
      )}
      <Button size="small" onClick={open} sx={{ textTransform: 'none', color: ACCENT, minWidth: 0, py: 0 }}>Wijzigen</Button>
    </Box>
  );
}
